import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import PageHeader from '../common/PageHeader';
import { Link } from 'react-router-dom';
import { getAuthHeaders } from '../../services/authSession';
import PositionCriteriaFields from '../recruitment/PositionCriteriaFields';
import ExistingCandidatesDialog from '../recruitment/ExistingCandidatesDialog';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import onboardingService from '../../services/onboardingService';
import MenuItem from '@mui/material/MenuItem';

const API_URL = process.env.REACT_APP_API_URL || '';
const APP_PUBLIC_URL = window.location.origin;

function getApplyLink(token) {
  return `${APP_PUBLIC_URL}/#/apply/${token}`;
}

async function apiFetch(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders(), ...(options.headers || {}) },
    ...options,
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
  return d;
}

const EMPTY_FORM = { title: '', description: '', position: '', startDate: '', deadline: '', postedUrl: '', image: '', isActive: true, headcount: 1, criteria: {}, defaultOnboardingTemplateId: '' };

/**
 * Sa vende ka mbetur kjo pozitë.
 *
 * Numri i të punësuarve NUK ruhet te konkursi — derivohet nga aplikimet, ndaj
 * s'mund të dalë jashtë sinkronie. Faqja vetëm e shfaq atë që kthen serveri.
 */
const FILL_META = {
  open:    { label: 'I hapur',        color: 'default' },
  partial: { label: 'Pjesërisht',     color: 'warning' },
  filled:  { label: 'I plotësuar',    color: 'success' },
};

export default function JobPostingsList() {
  const [postings, setPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null); // null = add, object = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snack, setSnack] = useState('');
  // «Gjej kandidatë ekzistues» — kërkimi historik hapet nga vetë pozita, sepse
  // aty lind pyetja: sapo u hap kjo pozitë, a kemi folur tashmë me dikë të tillë.
  const [findFor, setFindFor] = useState(null);
  // Shabllonet e onboarding-ut: pozita mund të propozojë një, dhe HR-ja e
  // ndryshon gjithsesi para se të nisë procesin.
  const [onbTemplates, setOnbTemplates] = useState([]);

  useEffect(() => {
    onboardingService().templates()
      .then((d) => setOnbTemplates(Array.isArray(d) ? d.filter((t) => t.isActive) : []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch('/api/job-postings');
      setPostings(d.data || []);
    } catch (e) {
      setSnack('Gabim gjatë ngarkimit: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditItem(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setDialogOpen(true);
  };

  /**
   * Redaktimi e ngarkon konkursin të plotë.
   *
   * Lista nuk e sjell më banner-in — ai ishte 700 KB për një rresht që s'e
   * shfaqte. Fotoja lexohet vetëm kur hapet formulari, dhe vetëm për atë rresht.
   */
  const openEdit = async (item) => {
    setEditItem(item);
    setForm({
      title: item.title || '',
      description: item.description || '',
      position: item.position || '',
      startDate: item.startDate || '',
      deadline: item.deadline || '',
      postedUrl: item.postedUrl || '',
      image: item.image || '',
      isActive: item.isActive,
      headcount: item.headcount || 1,
      criteria: item.criteria || {},
      defaultOnboardingTemplateId: item.defaultOnboardingTemplateId ? String(item.defaultOnboardingTemplateId) : '',
    });
    setFormError('');
    setDialogOpen(true);
    if (item.hasImage) {
      try {
        const d = await apiFetch(`/api/job-postings/${item.id}`);
        setForm((f) => ({ ...f, image: d.data?.image || '', criteria: d.data?.criteria || f.criteria }));
      } catch (_) { /* banner-i mbetet bosh; ruajtja nuk e fshin atë që s'u dërgua */ }
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setFormError('Titulli është i detyrueshëm'); return; }
    if (form.startDate && form.deadline && form.startDate > form.deadline) {
      setFormError('Data e fillimit s\'mund të jetë pas afatit të aplikimit');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      if (editItem) {
        const d = await apiFetch(`/api/job-postings/${editItem.id}`, {
          method: 'PUT',
          body: JSON.stringify(form),
        });
        setPostings(p => p.map(x => x.id === editItem.id ? d.data : x));
        setSnack('Pozita u përditësua!');
      } else {
        const d = await apiFetch('/api/job-postings', {
          method: 'POST',
          body: JSON.stringify(form),
        });
        setPostings(p => [d.data, ...p]);
        setSnack('Pozita u krijua! Linku publik është gati.');
      }
      setDialogOpen(false);
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (item) => {
    try {
      const d = await apiFetch(`/api/job-postings/${item.id}/toggle`, { method: 'PATCH' });
      setPostings(p => p.map(x => x.id === item.id ? d.data : x));
    } catch (e) {
      setSnack('Gabim: ' + e.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiFetch(`/api/job-postings/${id}`, { method: 'DELETE' });
      setPostings(p => p.filter(x => x.id !== id));
      setSnack('Pozita u fshi.');
    } catch (e) {
      setSnack('Gabim: ' + e.message);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(getApplyLink(token));
    setSnack('Linku u kopjua!');
  };

  const setField = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
  };

  // Job-posting banner image → stored as a base64 data URL (shown on the public
  // apply page). Kept small (≤ 2 MB) so it stays inline in the record.
  const onImagePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { setFormError('Zgjidhni një skedar imazhi (JPG, PNG, WEBP).'); return; }
    if (file.size > 2 * 1024 * 1024) { setFormError('Imazhi nuk duhet të kalojë 2 MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => { setForm(f => ({ ...f, image: String(reader.result) })); setFormError(''); };
    reader.readAsDataURL(file);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <PageHeader title="Pozitat" />
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" component={Link} to="/applications">← Kandidatët</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>Shto pozitë</Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Çdo pozitë gjeneron një <strong>link publik unik</strong> që mund t'ia ndani kandidatëve.
        Kandidatët plotësojnë formularin pa nevojë për llogari. Aplikimet ruhen automatikisht.
      </Alert>

      {loading ? (
        <Typography color="text.secondary">Duke ngarkuar...</Typography>
      ) : postings.length === 0 ? (
        <Card elevation={0} sx={{ border: '1px dashed', borderColor: 'divider', textAlign: 'center', p: 5 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>Nuk keni krijuar asnjë pozitë ende.</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>Krijo pozitën e parë</Button>
        </Card>
      ) : (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Titulli / Pozita</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Periudha e aplikimit</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Statusi</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Kandidatë</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Vende</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Link Publik</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Veprime</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {postings.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.title}</Typography>
                      {p.postedUrl && (
                        <Tooltip title={`Postuar te: ${p.postedUrl}`}>
                          <IconButton size="small" component="a" href={p.postedUrl} target="_blank" rel="noopener noreferrer" sx={{ p: 0.25 }}>
                            <OpenInNewIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    {p.position && <Typography variant="caption" color="text.secondary">{p.position}</Typography>}
                  </TableCell>
                  <TableCell>
                    {(p.startDate || p.deadline) ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {p.startDate
                            ? new Date(p.startDate).toLocaleDateString('sq-AL', { day: '2-digit', month: 'short' })
                            : '…'}
                          {' – '}
                          {p.deadline
                            ? new Date(p.deadline).toLocaleDateString('sq-AL', { day: '2-digit', month: 'short', year: 'numeric' })
                            : 'pa afat'}
                        </Typography>
                        {p.isUpcoming && <Chip size="small" label="Së shpejti" color="info" />}
                        {p.isExpired && <Chip size="small" label="Skaduar" color="error" />}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Pa afat</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Switch
                        size="small"
                        checked={p.isActive}
                        onChange={() => handleToggle(p)}
                        color="success"
                      />
                      <Chip
                        size="small"
                        label={p.isOpen ? 'Aktiv' : (p.isExpired ? 'Skaduar' : p.isUpcoming && p.isActive ? 'Së shpejti' : 'Joaktiv')}
                        color={p.isOpen ? 'success' : p.isUpcoming && p.isActive ? 'info' : 'default'}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <PeopleAltIcon fontSize="small" color="action" />
                      <Typography variant="body2">{p.applicationsCount}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {/* «2 nga 3» thotë më shumë se «3 vende»: HR-ja e di menjëherë
                        sa mbeten pa e hapur listën e kandidatëve. */}
                    <Stack spacing={0.25}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {p.hiredCount || 0} nga {p.headcount || 1}
                      </Typography>
                      <Chip
                        size="small"
                        label={(FILL_META[p.fillState] || FILL_META.open).label}
                        color={(FILL_META[p.fillState] || FILL_META.open).color}
                        sx={{ height: 18, fontSize: '0.68rem', width: 'fit-content' }}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title={getApplyLink(p.token)}>
                        <Typography
                          variant="caption"
                          sx={{
                            maxWidth: 160,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontFamily: 'monospace',
                            bgcolor: 'action.hover',
                            px: 1, py: 0.25,
                            borderRadius: 1,
                            display: 'block',
                          }}
                        >
                          {`/apply/${p.token.slice(0, 12)}...`}
                        </Typography>
                      </Tooltip>
                      <Tooltip title="Kopjo linkun">
                        <IconButton size="small" onClick={() => copyLink(p.token)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Hap formularin publik">
                        <IconButton size="small" component="a" href={getApplyLink(p.token)} target="_blank" rel="noopener noreferrer">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Gjej kandidatë ekzistues për këtë pozitë">
                      <IconButton size="small" onClick={() => setFindFor(p)}>
                        <PersonSearchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ndrysho">
                      <IconButton size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Fshij">
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirm(p)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editItem ? 'Ndrysho pozitën' : 'Pozitë e re'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Titulli i pozitës *"
                fullWidth
                value={form.title}
                onChange={setField('title')}
                placeholder="p.sh. Software Developer"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Pozita"
                fullWidth
                value={form.position}
                onChange={setField('position')}
                placeholder="p.sh. Junior Developer"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Numri i vendeve"
                type="number"
                fullWidth
                value={form.headcount}
                onChange={(e) => setForm(f => ({ ...f, headcount: e.target.value }))}
                inputProps={{ min: 1, max: 500 }}
                helperText="Sa persona kërkohen për këtë pozitë (1–500)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Shablloni i onboarding-ut"
                fullWidth
                value={form.defaultOnboardingTemplateId || ''}
                onChange={(e) => setForm(f => ({ ...f, defaultOnboardingTemplateId: e.target.value }))}
                helperText="Opsional — propozohet kur nis onboarding-u i një punësimi"
              >
                <MenuItem value="">Pa shabllon të parazgjedhur</MenuItem>
                {onbTemplates.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <PositionCriteriaFields
                value={form.criteria || {}}
                onChange={(criteria) => setForm(f => ({ ...f, criteria }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data e fillimit"
                type="date"
                fullWidth
                value={form.startDate}
                onChange={setField('startDate')}
                InputLabelProps={{ shrink: true }}
                helperText="Aplikimet pranohen nga kjo datë"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Afati i aplikimit"
                type="date"
                fullWidth
                value={form.deadline}
                onChange={setField('deadline')}
                InputLabelProps={{ shrink: true }}
                helperText="Lini bosh nëse s'ka afat"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Ku është postuar (URL)"
                fullWidth
                value={form.postedUrl}
                onChange={setField('postedUrl')}
                placeholder="p.sh. https://kosovajob.com/... ose linku i LinkedIn"
                helperText="Opsionale — linku i shpalljes në portal/rrjete, për referencë të brendshme"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Përshkrimi i pozitës"
                fullWidth
                multiline
                rows={4}
                value={form.description}
                onChange={setField('description')}
                placeholder="Përshkruani detyrat, kërkesat dhe kushtet e punës..."
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
                Imazhi / Banner (opsional)
              </Typography>
              {form.image ? (
                <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  <Box component="img" src={form.image} alt="Banner i pozitës"
                    sx={{ display: 'block', width: '100%', maxHeight: 200, objectFit: 'cover' }} />
                  <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <Button component="label" size="small" variant="contained" sx={{ textTransform: 'none', bgcolor: 'rgba(255,255,255,0.92)', color: 'text.primary', '&:hover': { bgcolor: '#fff' } }}>
                      Ndrysho
                      <input hidden type="file" accept="image/*" onChange={onImagePick} />
                    </Button>
                    <IconButton size="small" onClick={() => setForm(f => ({ ...f, image: '' }))}
                      sx={{ bgcolor: 'rgba(255,255,255,0.92)', '&:hover': { bgcolor: '#fff', color: 'error.main' } }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              ) : (
                <Box component="label" sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                  border: '2px dashed', borderColor: 'divider', borderRadius: 2, py: 2.5, px: 2, cursor: 'pointer',
                  transition: 'all .15s', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                }}>
                  <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 30, color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>Kliko për të ngarkuar imazhin e pozitës</Typography>
                  <Typography variant="caption" color="text.disabled">JPG, PNG, WEBP · deri 2 MB · shfaqet te faqja e aplikimit</Typography>
                  <input hidden type="file" accept="image/*" onChange={onImagePick} />
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))}
                  color="success"
                />
                <Typography variant="body2">
                  {form.isActive ? 'Pozitë aktive — pranon aplikime' : 'Joaktiv — nuk pranon aplikime'}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Anulo</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Duke ruajtur...' : editItem ? 'Ruaj ndryshimet' : 'Krijo pozitën'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Fshij pozitën?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Jeni i sigurt që dëshironi të fshini pozitën <strong>"{deleteConfirm?.title}"</strong>?
            {deleteConfirm?.applicationsCount > 0 && (
              <Box component="span" sx={{ color: 'warning.main' }}>
                {' '}Kjo pozitë ka {deleteConfirm.applicationsCount} kandidatë.
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Anulo</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm.id)}>Fshij</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={3500}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <ExistingCandidatesDialog
        posting={findFor}
        open={Boolean(findFor)}
        onClose={() => setFindFor(null)}
      />
    </Box>
  );
}
